import { Injectable, NgZone } from '@angular/core';
import { filter, flatMap, retry, tap } from 'rxjs/operators';
import { ActivationStart, Router, RouterEvent } from '@angular/router';
import { Auth, RecaptchaVerifier, ConfirmationResult, User, signInWithPopup, signInAnonymously, signInWithCustomToken, signInWithPhoneNumber, GoogleAuthProvider,OAuthProvider } from '@angular/fire/auth';
import { combineLatest, from, Observable, ReplaySubject, Subscription, throwError, timer } from 'rxjs';
import { ApiService } from './api.service';
import { OrderService } from './order.service';
import { AuthorizedApiService } from './authorized-api.service';
import { MessengerService } from './messenger.service';
import { Order, UserSpecificConfig } from "../models/Order";
import { ConfigService } from './config.service';
import { VisibilityService } from './visibility.service';
import {Identity} from '../models/Identity';
import {MiscUtils} from '../utils/Utils';
import { ProfileInfo } from '../models/Profile';
import {SnackbarService} from './snackbar.service';
import {TableCodeService} from './table-code.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  identity?: Identity;
  firebaseUser?: User;
  venueId?: string;
  signInProgress = false;
  private messengerSuccessCount = 0;
  private identitySubject = new ReplaySubject<Identity>(1);
  public profileInfo$ = new ReplaySubject<ProfileInfo | null>(1);
  public tokenSubject: ReplaySubject<{user: User, tokenVenueId?: string}> = new ReplaySubject(1);
  private cfgSub?: Subscription;
  private recaptchaVerifier?: RecaptchaVerifier;
  private confirmationResult?: ConfirmationResult;
  private failedToMigrateAnonUser: User | null = null;

  // constructor(
  //   private auth: Auth,  // Changed from AngularFireAuth

  // firebaseApp ref seemed to fix an strange issue in mobile safari
  public kioskMode: boolean = false;
  private kioskState?: string;
  constructor(private route: Router, private firebaseAuth: Auth, private authApiService: AuthorizedApiService,
              private apiService: ApiService, private orderService: OrderService,
              private ngZone: NgZone, private configService: ConfigService,
              private visibilityService: VisibilityService, private snackarService: SnackbarService,
              private tableCodes: TableCodeService) {
    this.init();
  }

  private init(): void {
    this.profileInfo$.next(null);
    console.log("XXXXX init AuthService...");
    this.kioskMode = localStorage.getItem("kiosk_mode") === "true";
    this.kioskState = localStorage.getItem("kiosk_state") || "standard";
    this.setupTokenListner();

    combineLatest([
      this.route.events.pipe(filter((event): event is ActivationStart => event instanceof ActivationStart)),
      this.tokenSubject,
      //this.visibilityService.visibilityChange$
    ]).subscribe(res => {
      const event = res[0];
      const userTup = res[1];
      // visibility checks if the browser tab is visible or not. This prevents auth logic from running in parallell on multiple tabs.
      // const visibility = res[2];
      // if (!visibility) {
      //   console.log("XXXXX visibility hidden, skip auth logic...");
      //   return;
      // }
      let user : User | null = userTup.user;
      const tokenVenueId = userTup.tokenVenueId;

      console.log(`Auth url: ${event.snapshot.url}`);
      console.log(`Auth uid: ${user?.uid}, anonymous: ${user?.isAnonymous}`);
      console.log(`tokenVenueId: ${tokenVenueId}`);

      //1. venue change
      //2. ml_link change from null to a value
      //3. user changed to not null (logged in)
      //4. user changed to null (logged out)
      //5. user changed (new token)
      //6. User signed in with new/changed table_code
      //(other changes should not trigger run of logic)

      const venueId = MiscUtils.getRouteParam(event.snapshot, "venue_id");
      const venueChanged = this.venueId !== venueId;
      this.venueId = venueId!;
      console.log("Venue id: ", this.venueId);
      const tableCode = event.snapshot.queryParamMap.get("table");
      // Only a table the server has not been told about yet for this user (the URL keeps ?table=
      // across navigations and reloads, Trello 4566)
      this.tableCodes.uid = user?.uid ?? null;
      const tableCodeChanged = tableCode != null && !this.tableCodes.isApplied(tableCode);
      const firebaseUserHasChanged = this.firebaseUser?.uid !== user?.uid;
      const oldFirebaseUser = this.firebaseUser;
      this.firebaseUser = user!;

      this.identity = new Identity();
      this.identity.venueId = venueId!;
      this.authApiService.setIdentity(this.identity);

      const urlSec = event.snapshot.queryParamMap.get("sec");
      const urlUid = event.snapshot.queryParamMap.get("uid");
      const urlUidNotSameAsLoggedInUser = urlUid != null && urlUid !== this.firebaseUser?.uid;
      if (urlSec != null && urlUidNotSameAsLoggedInUser) {
        console.log(`XXXXX urlUid ${urlUid} !== firebase user uid ${this.firebaseUser?.uid}`);
        console.log(`Clearing user"`, user);
        user = null;
      }

      if (user != null && firebaseUserHasChanged) {
        // @ts-ignore
        this.performServerSigninActions(this.venueId, oldFirebaseUser, tableCode);
      } else {
        if (urlSec != null && urlUidNotSameAsLoggedInUser) {
          this.signInUrlToken(urlSec, urlUid);
        } else if (this.firebaseUser == null) {
          this.signInAnonymously();
        } else if (venueChanged) {
          this.orderService.fetchCurrentOrder(tableCode!);
        } else if (tableCodeChanged) {
          this.orderService.changeTable(tableCode);
        } else {
          this.assureKioskMode();
        }
      }
      this.observeVenueConfig();
    });
  }

  ngOnDestroy(): void {
    this.cfgSub?.unsubscribe();
  }

  private observeVenueConfig() {
      this.cfgSub = this.configService.observeConfig(this.venueId!).subscribe(config => {
        this.kioskState = config?.kiosk_state;
      });
  }

  private setupTokenListner() {
    this.firebaseAuth.onAuthStateChanged(user => {
      console.log("XXXXX onAuthStateChanged");
      if (user != null) {
        this.profileInfo$.next({
          email: user.email!,
          name: user.displayName,
          image: user.photoURL!,
          venues: []
        });
        this.fetchIdTokenResult(user);
      } else {
        // @ts-ignore
        this.tokenSubject.next({user});
        this.profileInfo$.next(null);
      }
    });
  }

  private fetchIdTokenResult(user: User, attempt = 0) {
    user.getIdTokenResult().then((idTokenResult) => {
      console.log(idTokenResult);
      // @ts-ignore
      const tokenVenueId = idTokenResult?.claims?.venue_id?.toString();
      this.tokenSubject.next({user, tokenVenueId});
    }).catch((error) => {
      console.log(error);
      if (attempt < 4) {
        const delay = 1000 * (attempt + 1);
        console.log(`XXXXX getIdTokenResult failed, retrying in ${delay}ms...`);
        setTimeout(() => this.fetchIdTokenResult(user, attempt + 1), delay);
      }
    });
  }

  private signInAnonymously(attempt = 0) {
    if (this.signInProgress) {
      console.log("XXXXX skip sign in anon, since we are already processing another sign in...");
      return;
    }
    console.log("XXXXX signInAnonymously...");
    this.signInProgress = true;
    signInAnonymously(this.firebaseAuth).then(userCred => {
      console.log(userCred);
    }).catch(error => {
      console.log(error);
      if (attempt < 4) {
        const delay = 1000 * (attempt + 1);
        console.log(`XXXXX signInAnonymously failed, retrying in ${delay}ms...`);
        setTimeout(() => this.signInAnonymously(attempt + 1), delay);
      }
    }).finally(() => {
      this.setProgressToFalse();
    });
  }

  private setProgressToFalse() {
    this.ngZone.run(() => {
      this.signInProgress = false;
    });
  }

  private signInUrlToken(urlSec: string, uid: string) {
    console.log("XXXXX signInUrlToken...");
    return this.apiService.getCustomTokenForUrlToken(urlSec, uid).pipe(flatMap(token => {
      console.log(`Got custom token ${token}`);
      return from(signInWithCustomToken(this.firebaseAuth, token));
    })).subscribe(t => {
      console.log(t);
    }, e => {
      console.log("Error", e);
    });
  }

  private performServerSigninActions(venueId: string, oldFirebaseUser: User | null, tableCode: string | null) {
    console.log("XXXXX performServerSigninActions...");
    console.time("performServerSigninActions");
    // A table this user already has on the server (same tab, e.g. a reload) is not sent again
    const sendCode = this.tableCodes.isApplied(tableCode) ? null : tableCode;
    const ofu = this.failedToMigrateAnonUser != null ? this.failedToMigrateAnonUser : oldFirebaseUser;
    return this.apiService.signInFromFirebase(this.firebaseUser!, sendCode, venueId, ofu).pipe(
      retry({
        count: 3,
        delay: (error, retryCount) => {
          // Retry only on network errors / server errors, not on business errors (e.g. GURU-1001). A code the
          // venue does not know comes back as a 500 too, and does not get better with retries.
          const isTransient = (error?.status === 0 || error?.status >= 500) && !TableCodeService.isWrongCode(error);
          if (!isTransient) {
            return throwError(() => error);
          }
          console.log(`XXXXX signInFromFirebase failed (status ${error?.status}), retry ${retryCount}...`);
          return timer(1000 * retryCount);
        }
      })
    ).subscribe(order => {
      console.timeEnd("performServerSigninActions");
      console.log(`Got server order for user ${order.user_cfg?.user_id}`);
      this.tableCodes.markApplied(sendCode);
      this.failedToMigrateAnonUser = null;
      this.orderService.setState(order);
      console.log("Posting this.identity to authApiService", this.identity);
      this.identitySubject.next(this.identity!);
    }, error => {
      console.error("Server signin failed", error);
      this.setProgressToFalse();
      if (sendCode != null && TableCodeService.isWrongCode(error)) {
        // A stale QR or a mistyped link: without the code the guest still gets the menu, and the checkout asks for
        // the table. With the code left in the URL every reload would fail the same way.
        console.log(`XXXXX table code ${sendCode} rejected by the venue, dropping it`);
        this.dropTableCode();
        this.performServerSigninActions(venueId, oldFirebaseUser, null);
        return;
      }
      const failedToMigrate = error.error?.message?.includes("GURU-1001");
      if (failedToMigrate) {
        // Om man misslyckas migrera en användare, så logga ut användaren
        // Detta medför att gästen loggas in som en ny anonym användare
        // När gästen sedan loggar in på server med signInFromFirebase kovertera den gamla anonyma ordern till den nya anonyma användaren
        // Konverteringen sker för att det är inte möjligt att logga in på ett anonymt konto
        console.log("User failed to migrate, sign out user");
        this.firebaseAuth.signOut().then(() => {
          console.log("User signed out due to server signin failure");
          this.failedToMigrateAnonUser = oldFirebaseUser;
          this.snackarService.showMessageForError(error, 3000000);
        });
      }
    }, () => { this.setProgressToFalse(); });
  }

  /** Forgets the scanned code and takes ?table= out of the address bar, in place (no history entry). */
  private dropTableCode() {
    this.tableCodes.forget();
    const tree = this.route.parseUrl(this.route.url);
    if (tree.queryParams['table'] == null) return;
    const { table, ...rest } = tree.queryParams;
    tree.queryParams = rest;
    this.route.navigateByUrl(tree, { replaceUrl: true });
  }

  public async logout(persistIdentity = false, manuallySignedOut = true): Promise<void> {
    await this.firebaseAuth.signOut();
  }

  public async googleAuth(): Promise<void> {
    return this.signInWithProvider("google");
  }

  public async appleAuth(): Promise<void> {
    return this.signInWithProvider("apple");
  }

  private getProviderObject(provider: string): GoogleAuthProvider | OAuthProvider {
    switch (provider.toLowerCase()) {
      case 'google':
        return new GoogleAuthProvider();
      case 'apple':
        return new OAuthProvider('apple.com');
      default:
        throw new Error(`Unsupported provider: ${provider}`);
    }
  }

  private async signInWithProvider(provider: string): Promise<void> {
    try {
      console.log("About to sign in with provider:", provider);
      this.signInProgress = true;
      const authProvider = this.getProviderObject(provider);
      authProvider.setCustomParameters({ prompt: "select_account" });
      await signInWithPopup(this.firebaseAuth, authProvider);
      console.log("Signed in");
    } catch (error) {
      this.setProgressToFalse();
      console.log("Provider sign in failed... Error:", provider, error);
    }
  }

  public observeIdentity(): ReplaySubject<Identity> {
    return this.identitySubject;
  }

  private preventSpammyPage() {
    console.log("Page seems stuck in a loop, putting it out of its misery...");
    window.location.href="about:blank";
  }

  private reloadWithNewVenueId(venueId: string) {
    console.log("Reload with new venueId....");
    const index = window.location.href.indexOf("/", 10);
    const url = `${window.location.href.substring(0, index)}/${venueId}/menu/food`;
    console.log(url);
    window.location.replace(url);
  }

  activateKioskMode(): Observable<Order> {
    const table = this.orderService.getOrderOrNull()?.user_cfg.table;
    // @ts-ignore
    return this.setupKioskModeForUser(table);
  }

  deactivateKioskMode(): Observable<Order> {
    const table = this.orderService.getOrderOrNull()?.user_cfg.table;
    return this.setupKioskModeForUser("0");
  }

  private assureKioskMode() {
    if (this.kioskMode) {
      //console.log(`Kiosk mode detected, validate state...`);
      const kt = localStorage.getItem("kiosk_table");
      const userCfg = this.orderService.getOrderOrNull()?.user_cfg;
      if (kt && userCfg && (userCfg.kiosk_mode !== true || userCfg.table !== kt || userCfg.must_scan)) {
        console.log(`Kiosk mode detected, state is not correct, setup kiosk mode...`);
        this.setupKioskModeForUser(kt).subscribe(value => {});
      }
    }
  }

  // Setup kiosk mode for user at server, table not null => active kiosk mode
  private setupKioskModeForUser(table: string): Observable<Order> {
    return this.configService.getNonEmptyConfig().pipe(flatMap(cfg => this.doSetupKioskModeForUser(table)));
  }

  // Setup kiosk mode for user at server, table not null => active kiosk mode
  private doSetupKioskModeForUser(table: string): Observable<Order> {
    return this.orderService.changeTable(undefined, table).pipe(tap(() => {
      if (table === "0") {
        console.log(`Disable kiosk mode for user`);
        localStorage.removeItem("kiosk_mode");
        localStorage.removeItem("kiosk_table");
        localStorage.removeItem("kiosk_state");
        this.kioskMode = false;
      } else {
        console.log(`Setup kiosk mode for user at table ${table}`);
        localStorage.setItem("kiosk_mode", "true");
        localStorage.setItem("kiosk_table", table);
        localStorage.setItem("kiosk_state", this.kioskState!)
        this.kioskMode = true;
      }
    }));
  }

  isAnonOrderable(userConfig?: UserSpecificConfig): boolean {
    const ao = userConfig?.is_anon_orderable;
    return ao === true || this.kioskMode;
  }

  public isKioskMode(): boolean {
    return this.kioskMode;
  }

  public getKioskState(): string {
    return this.kioskState || "standard";
  }

  public initializeRecaptcha(containerId: string): void {
    console.log("Initializing reCAPTCHA...");
    this.recaptchaVerifier = new RecaptchaVerifier(this.firebaseAuth, containerId, {
      size: 'invisible', // Use 'normal' if you want the reCAPTCHA to be visible
      callback: (response: string) => {
        console.log('reCAPTCHA solved');
      },
      'expired-callback': () => {
        console.log('reCAPTCHA expired');
        this.recaptchaVerifier!.clear();
      },
    });
    console.log(`reCAPTCHA initialized ${this.recaptchaVerifier}`);
  }

  public sendVerificationCode(phoneNumber: string): Promise<void> {
    if (!this.recaptchaVerifier) {
      return Promise.reject(new Error('reCAPTCHA is not initialized. Please refresh the page and try again.'));
    }
    
    return signInWithPhoneNumber(this.firebaseAuth, phoneNumber, this.recaptchaVerifier)
      .then((confirmationResult) => {
        this.confirmationResult = confirmationResult;
        console.log('Verification code sent to', phoneNumber);
      })
      .catch((error) => {
        console.error('Error sending verification code:', error);
        // Reset recaptcha on error
        if (this.recaptchaVerifier) {
          this.recaptchaVerifier.clear();
        }
        throw error;
      });
  }

  public verifyCode(code: string): Promise<void> {
    if (!this.confirmationResult) {
      return Promise.reject(new Error('No confirmation result available. Please request a new verification code.'));
    }
    
    // Add a timeout to prevent hanging
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Verification timeout. Please try again.'));
      }, 30000); // 30 second timeout
    });
    
    return Promise.race([
      this.confirmationResult.confirm(code),
      timeoutPromise
    ])
      .then((result) => {
        const user = result.user;
        console.log('User signed in successfully:', user);
        // Perform any additional actions with the signed-in user
      })
      .catch((error) => {
        console.error('Error during code verification:', error);
        // Reset confirmation result on error to allow retry
        this.confirmationResult = undefined;
        throw error;
      });
  }
}
